import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, SpinnerGap, Target, Phone, Megaphone,
  BookOpen, Users, Pencil, Lightning,
} from "@phosphor-icons/react";

const GOALS = [
  { id: "grow_list", label: "Grow email list or subscribers", icon: Users },
  { id: "drive_calls", label: "Drive consultations or discovery calls", icon: Phone },
  { id: "promote_offer", label: "Launch or promote a specific offer", icon: Megaphone },
  { id: "build_authority", label: "Build authority in a specific topic area", icon: BookOpen },
  { id: "retain_clients", label: "Retain and nurture existing clients", icon: Target },
  { id: "custom", label: "Custom goal", icon: Pencil },
];

const VOLUME_OPTIONS = [4, 8, 12, 16, 20];

const PROCESSING_MSGS = [
  "Setting your strategic direction...",
  "Mapping content to your goals...",
  "Distributing across the month...",
  "Your plan is ready.",
];

function getDefaultMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(m) {
  try { return new Date(m + "-01").toLocaleDateString("en-US", { month: "long", year: "numeric" }); }
  catch { return m; }
}

export default function PlanWizardPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const businessId = user?.active_business_id || "";

  const [step, setStep] = useState(1);
  const [planMonth, setPlanMonth] = useState(getDefaultMonth());
  const [title, setTitle] = useState(`${monthLabel(getDefaultMonth())} Content Plan`);
  const [selectedGoals, setSelectedGoals] = useState([]);
  const [customGoal, setCustomGoal] = useState("");
  const [audienceNotes, setAudienceNotes] = useState("");
  const [totalPieces, setTotalPieces] = useState(12);
  const [processingMsg, setProcessingMsg] = useState(0);

  const toggleGoal = (id) => {
    setSelectedGoals(prev => prev.includes(id) ? prev.filter(g => g !== id) : prev.length < 3 ? [...prev, id] : prev);
  };

  const effectiveGoals = selectedGoals.map(id => {
    if (id === "custom") return customGoal;
    return GOALS.find(g => g.id === id)?.label || id;
  }).filter(Boolean);

  const canProceed = () => {
    if (step === 1) return planMonth;
    if (step === 2) return effectiveGoals.length > 0;
    if (step === 3) return totalPieces >= 4;
    return true;
  };

  const handleGenerate = useCallback(async () => {
    setStep(4);
    setProcessingMsg(0);
    const interval = setInterval(() => setProcessingMsg(p => p < PROCESSING_MSGS.length - 1 ? p + 1 : p), 1800);

    try {
      const resp = await api.post("/plans/generate", {
        business_id: businessId,
        plan_month: planMonth,
        goals: effectiveGoals,
        target_audience_notes: audienceNotes,
        content_volume: { total_pieces: totalPieces },
      });
      clearInterval(interval);
      if (resp.data.error) {
        toast.error(resp.data.error_message || "Plan generation failed");
        setStep(3);
      } else {
        navigate(`/plans/${resp.data.plan_id}`);
      }
    } catch (err) {
      clearInterval(interval);
      toast.error(err.response?.data?.detail || "Plan generation failed.");
      setStep(3);
    }
  }, [api, businessId, planMonth, effectiveGoals, audienceNotes, totalPieces, navigate]);

  const goNext = () => step === 3 ? handleGenerate() : setStep(s => s + 1);

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Header />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {step >= 1 && step <= 3 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <button onClick={() => step > 1 ? setStep(s => s - 1) : navigate("/plans")} className="flex items-center gap-1.5 text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors" data-testid="plan-back-btn">
                <ArrowLeft size={16} /> {step === 1 ? "All Plans" : "Back"}
              </button>
              <span className="text-xs text-[var(--text-muted)] font-medium">Step {step} of 3</span>
            </div>
            <div className="flex gap-1.5">
              {[1, 2, 3].map(s => <div key={s} className={`flex-1 h-1 rounded-full transition-colors ${s <= step ? "bg-[#FF4400]" : "bg-[var(--border)]"}`} />)}
            </div>
          </div>
        )}

        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div key="s1" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} data-testid="plan-step-month">
              <h1 className="text-2xl sm:text-3xl font-bold text-[var(--text-primary)] font-['Cormorant_Garamond']">Plan your month</h1>
              <div className="mt-6 space-y-4">
                <div>
                  <label className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider mb-1.5 block">Month</label>
                  <Input type="month" value={planMonth} onChange={e => { setPlanMonth(e.target.value); setTitle(`${monthLabel(e.target.value)} Content Plan`); }} className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm" data-testid="plan-month-input" />
                </div>
                <div>
                  <label className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider mb-1.5 block">Plan Title</label>
                  <Input value={title} onChange={e => setTitle(e.target.value)} className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm" data-testid="plan-title-input" />
                </div>
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div key="s2" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} data-testid="plan-step-goals">
              <h1 className="text-2xl sm:text-3xl font-bold text-[var(--text-primary)] font-['Cormorant_Garamond']">What are your top priorities this month?</h1>
              <p className="text-sm text-[var(--text-muted)] mt-2">Select up to 3 goals</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
                {GOALS.map(g => (
                  <button key={g.id} onClick={() => toggleGoal(g.id)} data-testid={`plan-goal-${g.id}`}
                    className={`flex items-start gap-3 p-4 rounded-lg border text-left transition-all ${selectedGoals.includes(g.id) ? "bg-[#FF4400]/5 border-[#FF4400]/40" : "bg-[var(--bg-card)] border-[var(--border)] hover:border-[var(--border-strong)]"}`}>
                    <g.icon size={18} className={`mt-0.5 shrink-0 ${selectedGoals.includes(g.id) ? "text-[#FF4400]" : "text-[var(--text-muted)]"}`} weight={selectedGoals.includes(g.id) ? "fill" : "regular"} />
                    <span className={`text-sm ${selectedGoals.includes(g.id) ? "text-[var(--text-primary)] font-medium" : "text-[var(--text-secondary)]"}`}>{g.label}</span>
                  </button>
                ))}
              </div>
              {selectedGoals.includes("custom") && (
                <Input value={customGoal} onChange={e => setCustomGoal(e.target.value)} placeholder="Describe your custom goal..." className="mt-3 bg-[var(--bg-card)] border-[var(--border-strong)] text-sm" data-testid="plan-custom-goal" />
              )}
              <div className="mt-5">
                <label className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider mb-1.5 block">Audience notes for this month <span className="normal-case">(optional)</span></label>
                <Textarea value={audienceNotes} onChange={e => setAudienceNotes(e.target.value)} placeholder='e.g. "Targeting early-stage founders" or "Focusing on Q2 launch campaigns"' rows={2} className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm resize-y" data-testid="plan-audience-notes" />
              </div>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div key="s3" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} data-testid="plan-step-volume">
              <h1 className="text-2xl sm:text-3xl font-bold text-[var(--text-primary)] font-['Cormorant_Garamond']">How much content do you want to plan?</h1>
              <div className="mt-6 space-y-6">
                <div className="flex items-center justify-center gap-2">
                  {VOLUME_OPTIONS.map(v => (
                    <button key={v} onClick={() => setTotalPieces(v)} data-testid={`volume-${v}`}
                      className={`w-12 h-12 rounded-xl text-sm font-bold transition-all ${totalPieces === v ? "bg-[#FF4400] text-white shadow-lg scale-110" : "bg-[var(--bg-card)] text-[var(--text-secondary)] border border-[var(--border)] hover:border-[var(--border-strong)]"}`}>
                      {v}
                    </button>
                  ))}
                </div>
                <p className="text-center text-sm text-[var(--text-muted)]">{totalPieces} pieces across 4 weeks</p>
                <p className="text-xs text-[var(--text-muted)] text-center">You can adjust the mix after the AI generates your plan</p>
              </div>
            </motion.div>
          )}

          {step === 4 && (
            <motion.div key="processing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center justify-center py-20 space-y-8" data-testid="plan-processing">
              <SpinnerGap size={64} className="text-[#FF4400] animate-spin" />
              <AnimatePresence mode="wait">
                <motion.p key={processingMsg} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="text-base text-[var(--text-secondary)] font-medium">
                  {PROCESSING_MSGS[processingMsg]}
                </motion.p>
              </AnimatePresence>
              <div className="flex gap-1.5">
                {PROCESSING_MSGS.map((_, i) => <div key={`dot-${i}`} className={`w-2 h-2 rounded-full transition-colors ${i <= processingMsg ? "bg-[#FF4400]" : "bg-[var(--border)]"}`} />)}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {step >= 1 && step <= 3 && (
          <div className="flex justify-end mt-8">
            <Button onClick={goNext} disabled={!canProceed()} className="bg-[#FF4400] hover:bg-[#FF6633] text-white px-6 h-10 text-sm font-semibold disabled:opacity-40" data-testid="plan-next-btn">
              {step === 3 ? <><Lightning size={16} className="mr-1.5" /> Generate My Content Plan</> : <>Next <ArrowRight size={16} className="ml-1.5" /></>}
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
